/**
 * Transport Pairing Helpers
 *
 * Groups inbound (TRANSHIN) + outbound (TRANSRÜCK) PPS transport services into a single
 * UI "pair" group so the user picks both directions with one selection.
 * DaVinci requires both legs to be present in the BA payload; this helper exists to make
 * that impossible to forget in the UI layer.
 *
 * All logic is pure; the checkout store stays keyed by BookingCodeId and is unaware of pairing.
 */
import type { PpsService, PpsServiceType } from '@/types/schemas/pps';
import { getServiceTypeTotalPrice, getServiceTypeAvailability } from '@/types/schemas/pps';
import { isHiddenAuxiliaryAvailability } from '@/constants/extras/availability';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import type {
  ExtraGroupDisplayItem,
  ExtraGroupPair,
  ExtraGroupSingle,
  ServiceTypeDisplayItem,
} from './useCheckoutPps.types';

export const TRANSPORT_INBOUND_GROUP = 'TRANSHIN';
export const TRANSPORT_OUTBOUND_GROUP = 'TRANSRÜCK';

type Availability = 'available' | 'on-request' | 'unavailable';

function normalizeGroup(name: string | undefined): string {
  return (name ?? '').trim().toUpperCase();
}

export function isInboundTransport(service: PpsService): boolean {
  return (
    service.Requirement === 'T' &&
    normalizeGroup(service.PackageGroupName) === TRANSPORT_INBOUND_GROUP
  );
}

export function isOutboundTransport(service: PpsService): boolean {
  return (
    service.Requirement === 'T' &&
    normalizeGroup(service.PackageGroupName) === TRANSPORT_OUTBOUND_GROUP
  );
}

// Degrade to the worst-case: unavailable > on-request > available.
export function worstAvailability(a: Availability, b: Availability): Availability {
  if (a === 'unavailable' || b === 'unavailable') return 'unavailable';
  if (a === 'on-request' || b === 'on-request') return 'on-request';
  return 'available';
}

function wrapperClassFor(index: number, total: number): string | undefined {
  return index === total - 1 ? undefined : 'pb-5 mb-5 border-b border-opacity-50';
}

function toSingleGroup(extra: PpsService): ExtraGroupSingle {
  const visibleTypes = (extra.ServiceTypes ?? []).filter(
    (st) => !isHiddenAuxiliaryAvailability(st.Availability)
  );
  const serviceTypes: ServiceTypeDisplayItem[] = visibleTypes.map((st, index, arr) => {
    const totalPrice = getServiceTypeTotalPrice(st);
    return {
      id: st.Code,
      title: st.Code_Name,
      price: formatPrice(totalPrice, true),
      priceRaw: totalPrice,
      availability: getServiceTypeAvailability(st),
      wrapperClass: wrapperClassFor(index, arr.length),
    };
  });

  return {
    kind: 'single',
    id: extra.BookingCodeId,
    title: extra.BookingCodeName,
    serviceTypes,
  };
}

function toPairGroup(
  inbound: PpsService,
  outbound: PpsService,
  title: string
): ExtraGroupPair | null {
  const inTypes = inbound.ServiceTypes ?? [];
  const outByCode = new Map<string, PpsServiceType>();
  for (const st of outbound.ServiceTypes ?? []) {
    outByCode.set(st.Code, st);
  }

  const dropped: string[] = [];
  const kept: Array<{ inSt: PpsServiceType; outSt: PpsServiceType }> = [];
  for (const inSt of inTypes) {
    const outSt = outByCode.get(inSt.Code);
    if (!outSt) {
      dropped.push(inSt.Code);
      continue;
    }
    if (
      isHiddenAuxiliaryAvailability(inSt.Availability) ||
      isHiddenAuxiliaryAvailability(outSt.Availability)
    ) {
      outByCode.delete(inSt.Code);
      continue;
    }
    kept.push({ inSt, outSt });
    outByCode.delete(inSt.Code);
  }
  for (const code of outByCode.keys()) {
    dropped.push(code);
  }

  if (dropped.length > 0) {
    console.warn(
      `[CheckoutPps] Dropped transport service types not present on both legs (${inbound.BookingCodeName} \u2194 ${outbound.BookingCodeName}):`,
      dropped
    );
  }

  if (kept.length === 0) {
    console.warn(
      `[CheckoutPps] Transport pair has no overlapping service types; skipping pair (${inbound.BookingCodeName} \u2194 ${outbound.BookingCodeName})`
    );
    return null;
  }

  const serviceTypes: ServiceTypeDisplayItem[] = kept.map(({ inSt, outSt }, index) => {
    const priceRaw = getServiceTypeTotalPrice(inSt) + getServiceTypeTotalPrice(outSt);
    const availability = worstAvailability(
      getServiceTypeAvailability(inSt),
      getServiceTypeAvailability(outSt)
    );
    return {
      id: inSt.Code,
      title: inSt.Code_Name || outSt.Code_Name,
      price: formatPrice(priceRaw, true),
      priceRaw,
      availability,
      wrapperClass: wrapperClassFor(index, kept.length),
    };
  });

  return {
    kind: 'pair',
    id: `pair:${inbound.BookingCodeId}:${outbound.BookingCodeId}`,
    inboundId: inbound.BookingCodeId,
    outboundId: outbound.BookingCodeId,
    title,
    serviceTypes,
  };
}

/**
 * Build UI-ready extras groups from a flat list of PPS services.
 * - Transport legs (Requirement:"T" + PackageGroupName TRANSHIN/TRANSRÜCK) are paired positionally.
 * - A transport leg only renders when it can be successfully paired with its
 *   counterpart — booking requires both legs together, so a lone leg (missing
 *   counterpart, no overlapping ServiceType codes, or all entries hidden by
 *   availability) is suppressed instead of falling back to single-rendering.
 * - All non-transport extras render as single groups.
 * - Input order is preserved: each group's position is the position of its first-seen PPS service.
 */
export function buildExtrasGroups(
  extras: ReadonlyArray<PpsService>,
  pairTitle: string
): ExtraGroupDisplayItem[] {
  const usable = extras.filter((e) => (e.ServiceTypes?.length ?? 0) > 0);

  const inboundQueue = usable.filter(isInboundTransport);
  const outboundQueue = usable.filter(isOutboundTransport);

  const transportLegIds = new Set<number>();
  for (const leg of inboundQueue) transportLegIds.add(leg.BookingCodeId);
  for (const leg of outboundQueue) transportLegIds.add(leg.BookingCodeId);

  const pairByInboundId = new Map<number, ExtraGroupPair>();

  const pairCount = Math.min(inboundQueue.length, outboundQueue.length);
  for (let i = 0; i < pairCount; i++) {
    const inbound = inboundQueue[i];
    const outbound = outboundQueue[i];
    const pair = toPairGroup(inbound, outbound, pairTitle);
    if (pair) {
      pairByInboundId.set(inbound.BookingCodeId, pair);
    }
  }

  for (let i = pairCount; i < outboundQueue.length; i++) {
    console.warn(
      `[CheckoutPps] Unpaired outbound transport leg: ${outboundQueue[i].BookingCodeName}`
    );
  }
  for (let i = pairCount; i < inboundQueue.length; i++) {
    console.warn(
      `[CheckoutPps] Unpaired inbound transport leg: ${inboundQueue[i].BookingCodeName}`
    );
  }

  const groups: ExtraGroupDisplayItem[] = [];
  for (const extra of usable) {
    const pair = pairByInboundId.get(extra.BookingCodeId);
    if (pair) {
      groups.push(pair);
      continue;
    }
    if (transportLegIds.has(extra.BookingCodeId)) continue;

    const single = toSingleGroup(extra);
    if (single.serviceTypes.length === 0) continue;
    groups.push(single);
  }

  return groups;
}
